import { useCallback, useEffect, useRef, useState } from "react";

// Maps SpeechRecognition error codes to user-friendly messages.
const ERROR_MESSAGES = {
  "not-allowed":
    "Microphone access was denied. Allow it in your browser settings.",
  "service-not-allowed":
    "Microphone access was denied. Allow it in your browser settings.",
  "audio-capture": "No microphone found.",
  network: "Network error during speech recognition.",
};
const DEFAULT_ERROR = "Voice input failed. Please try again.";

// Errors that are benign during continuous dictation — the browser ends the
// session on silence, but we restart it so the user can pause to think.
const BENIGN_ERRORS = new Set(["no-speech", "aborted"]);

const getSpeechRecognition = () =>
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : undefined;

/**
 * Browser-native speech-to-text via the Web Speech API.
 * Returns an object (project hook convention). The transcript is delivered
 * through `onResult` so the caller decides where the text goes.
 *
 * Listening is continuous: short pauses do not end the session. The browser
 * still stops on prolonged silence, so we restart it automatically — the mic
 * only truly stops when the user calls `stop()` (clicks the button again).
 */
function useVoiceInput({ onResult, onError, lang = "en-US" } = {}) {
  const SpeechRecognition = getSpeechRecognition();
  const isSupported = !!SpeechRecognition;

  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);

  // Whether the user still wants to be recording. Drives auto-restart on the
  // browser's silence-triggered `onend` and prevents restart after `stop()`.
  const wantRecordingRef = useRef(false);
  // Finalized text from earlier recognition runs of the current start()→stop()
  // session — moved here on each silence restart so nothing already said is
  // lost. Finals of the run in progress are rebuilt from `event.results` on
  // every event instead of being appended, because Chrome re-delivers
  // already-final results (resultIndex jumps back) and appending doubled them.
  const committedRef = useRef("");
  const runFinalsRef = useRef([]);

  // Keep latest callbacks without re-creating the recognition instance.
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onResultRef.current = onResult;
    onErrorRef.current = onError;
  }, [onResult, onError]);

  // Lazily build a single recognition instance.
  const getRecognition = useCallback(() => {
    if (!isSupported) return null;
    if (recognitionRef.current) return recognitionRef.current;

    const recognition = new SpeechRecognition();
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.lang = lang;

    recognition.onresult = (event) => {
      const finals = [];
      const interims = [];
      for (let i = 0; i < event.results.length; i += 1) {
        const result = event.results[i];
        const chunk = result?.[0]?.transcript?.trim();
        if (!chunk) continue;
        if (result.isFinal) finals.push(chunk);
        else interims.push(chunk);
      }
      // After a restart Chrome sometimes re-recognizes the tail of the audio
      // it already finalized, so the new run opens with the phrase the last
      // one closed on. Drop that echo.
      const lastCommitted = committedRef.current
        .split(" ")
        .slice(-finals[0]?.split(" ").length || 0)
        .join(" ");
      if (
        finals.length &&
        lastCommitted &&
        lastCommitted.toLowerCase() === finals[0].toLowerCase()
      ) {
        finals.shift();
      }
      runFinalsRef.current = finals;
      const combined = [committedRef.current, ...finals, ...interims]
        .filter(Boolean)
        .join(" ");
      if (combined && onResultRef.current) onResultRef.current(combined);
    };

    recognition.onerror = (event) => {
      const code = event?.error;
      // Silence/aborts are expected during a pause — let onend restart us.
      if (BENIGN_ERRORS.has(code)) return;
      wantRecordingRef.current = false;
      setIsRecording(false);
      const message = ERROR_MESSAGES[code] || DEFAULT_ERROR;
      if (onErrorRef.current) onErrorRef.current(message);
    };

    recognition.onend = () => {
      // The run is over: its finals are settled, so bank them before the next
      // run's `event.results` starts again from index 0.
      committedRef.current = [committedRef.current, ...runFinalsRef.current]
        .filter(Boolean)
        .join(" ");
      runFinalsRef.current = [];
      // Browser ended the session. If the user hasn't stopped, restart so
      // pauses (thinking of a word) keep the mic alive.
      if (wantRecordingRef.current) {
        try {
          recognition.start();
        } catch {
          wantRecordingRef.current = false;
          setIsRecording(false);
        }
      } else {
        setIsRecording(false);
      }
    };

    recognitionRef.current = recognition;
    return recognition;
  }, [isSupported, SpeechRecognition, lang]);

  const start = useCallback(() => {
    if (!isSupported || isRecording) return;
    const recognition = getRecognition();
    if (!recognition) return;
    committedRef.current = "";
    runFinalsRef.current = [];
    wantRecordingRef.current = true;
    try {
      recognition.start();
      setIsRecording(true);
    } catch {
      // .start() throws if already started — keep state consistent.
      wantRecordingRef.current = false;
      setIsRecording(false);
    }
  }, [isSupported, isRecording, getRecognition]);

  const stop = useCallback(() => {
    wantRecordingRef.current = false;
    const recognition = recognitionRef.current;
    if (recognition) recognition.stop();
    setIsRecording(false);
  }, []);

  const toggle = useCallback(() => {
    if (isRecording) stop();
    else start();
  }, [isRecording, start, stop]);

  // Tear down on unmount.
  useEffect(() => {
    return () => {
      wantRecordingRef.current = false;
      const recognition = recognitionRef.current;
      if (recognition) {
        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
        recognition.abort();
      }
    };
  }, []);

  return { isSupported, isRecording, start, stop, toggle };
}

export default useVoiceInput;
